import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { getAuthToken } from '../services/api';
import CommandCenterMap from './CommandCenterMap';
import '../styles/command-center.css';

// NGS Command Center — the 75" dispatch board.
//
// Answers, from across the room: what are we doing today, where is each crew, what are
// they on, how long have they been on it, and how is the day going.
//
// Crew columns ARE the schedule. An earlier layout had "today's schedule" and "crew
// status" as separate panels, but on a screen where space is the scarcest resource
// those are the same information twice — so each crew is a column with its current job
// at the top and its queue beneath.
//
// The timers remain the record. A clock-in already records arrival, so arrival time and
// time-on-site come from data the crews have been recording for two years — none of
// that depends on GPS and none of it changed.
//
// Collectors add a SECOND, independent account of where a crew is. It never overrides
// the timer and never edits one; it sits underneath as a quiet cross-check. Where the
// two disagree, that is information — usually a forgotten punch — for a human to look
// at, not for the software to act on.

const POLL_MS = 15000;
// The board rotates to a full-screen map and back. Full-screen rather than a side
// panel because at 10-15ft a cramped map is decorative — the dots need real size to be
// readable, and the elapsed counters must not give up width to get them.
const COLUMNS_MS = 40000;
const MAP_MS = 20000;
// A tap holds whichever view you're on. Someone walks up to look at the map, and it
// must not slide away mid-sentence.
const HOLD_MS = 120000;
// Past this the board says so rather than quietly showing stale numbers — nobody is
// watching a console on a wall display.
const STALE_MS = 60000;


const STATUS_LABEL = {
  on_site: 'On Site',
  office: 'In Office',
  paused: 'Paused',
  traveling: 'En Route',
  available: 'Available',
  off: 'Off',
};

function fmtElapsed(ms) {
  if (ms == null || ms < 0) return '—';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

function fmtClockTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function JobLines({ job }) {
  return (
    <>
      <div className="cc-job-number">{job.job_number || '—'}</div>
      <div className="cc-client">{job.client_name || job.property_owner || ''}</div>
      {job.address && <div className="cc-address">{job.address}</div>}
    </>
  );
}

// The collector's account of where this crew is, shown under the timer's.
//
// Deliberately one quiet line. The timer is what the board is for; this is a footnote
// that only earns attention when it contradicts the timer or goes silent.
function DeviceLine({ device }) {
  // Assigned but never reported — a collector waiting for the beacon to be installed
  // has nothing to say, and saying so on the wall every day is just noise.
  if (!device || device.signal === 'never') return null;

  const seen = fmtClockTime(device.last_seen_at);
  const wasAt = device.was_at ? ` · was at ${device.was_at.job_number}` : '';

  if (device.signal === 'off') {
    // Somebody pressed Stop on the tablet. Neutral, not alarming: it was a decision,
    // and the beacon told us so on its way out.
    return (
      <div className="cc-device is-quiet">
        Collector switched off {fmtClockTime(device.stopped_at) || seen}{wasAt}
      </div>
    );
  }
  if (device.signal === 'deferred') {
    // Quiet is this one's normal — poor coverage, or data kept off for battery.
    // Reporting it in red every afternoon would teach the room to ignore the state.
    return (
      <div className="cc-device is-quiet">Collector reports later · last heard {seen}{wasAt}</div>
    );
  }
  if (device.signal === 'dark') {
    // No live claim, but no longer silent about what we did know.
    return <div className="cc-device is-dark">Collector: no signal since {seen}{wasAt}</div>;
  }

  const lateNote = device.signal === 'stale' ? ` · last heard ${seen}` : '';
  const at = device.at;

  if (at && device.agrees === null) {
    // Sitting on a job site with no timer running. This is the single most useful
    // thing the tracking layer surfaces: someone is working and nobody clocked in.
    return (
      <div className="cc-device is-flag">
        Collector at {at.job_number} since {fmtClockTime(device.since)} · no timer running
      </div>
    );
  }
  if (at && device.agrees === false) {
    return (
      <div className="cc-device is-flag">
        Collector says {at.job_number} since {fmtClockTime(device.since)}
      </div>
    );
  }
  if (at) {
    return <div className="cc-device is-ok">Collector agrees{lateNote}</div>;
  }
  if (device.arriving) {
    // Dwell hasn't completed, so this isn't a visit yet — but saying "not at a known
    // site" here would claim an ignorance the server doesn't have.
    return (
      <div className="cc-device">Collector: arriving at {device.arriving.job_number}…</div>
    );
  }
  return <div className="cc-device">Collector: not at a known site{lateNote}</div>;
}

export default function CommandCenter() {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState('');
  const [lastOk, setLastOk] = useState(null);
  const [now, setNow] = useState(Date.now());
  // ?view=map or ?view=columns pins one view and stops the rotation. Same convention
  // the Map page uses for ?crews=1. Meant for a desk: rotation is right for an ambient
  // wall display and infuriating when you are actually testing something. Read in a
  // state initialiser rather than at module scope so it is per-mount, not per-import.
  const [pinnedView] = useState(
    () => new URLSearchParams(window.location.search).get('view')
  );
  const [view, setView] = useState(pinnedView === 'map' ? 'map' : 'columns');
  const [heldUntil, setHeldUntil] = useState(0);

  // Difference between the server's clock and this machine's, measured when the
  // payload lands. Elapsed times are computed against the server's notion of now, so a
  // NUC with a drifting clock can't put a crew on site for the wrong length of time.
  const skewRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const res = await axios.get(`${API_URL}/command-center/board`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.data?.server_time) {
        skewRef.current = new Date(res.data.server_time).getTime() - Date.now();
      }
      setBoard(res.data);
      setLastOk(Date.now());
      setError('');
    } catch (e) {
      // Keep the last good board on screen rather than blanking it; the stale banner
      // tells the room the numbers have stopped moving.
      setError(e.response?.data?.error || 'Cannot reach the server');
    }
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, POLL_MS);
    return () => clearInterval(poll);
  }, [load]);

  // Separate 1s tick so elapsed counters move smoothly between polls.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // View rotation. Re-armed on every switch rather than run off one interval, so the
  // two views can have different durations.
  useEffect(() => {
    if (pinnedView) return;                  // pinned by URL, never rotates
    if (Date.now() < heldUntil) return;
    const ms = view === 'columns' ? COLUMNS_MS : MAP_MS;
    const t = setTimeout(
      () => setView((v) => (v === 'columns' ? 'map' : 'columns')),
      ms
    );
    return () => clearTimeout(t);
  }, [view, heldUntil, pinnedView]);

  const serverNow = now + skewRef.current;
  const isStale = lastOk != null && now - lastOk > STALE_MS;

  if (!board) {
    return (
      <div className="cc-root">
        <div className="cc-center">{error || 'Loading the board…'}</div>
      </div>
    );
  }

  const { summary } = board;
  const pct = summary.total ? Math.round((summary.completed / summary.total) * 100) : 0;
  const dateLabel = new Date(`${board.date}T12:00:00`).toLocaleDateString([], {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  const tap = () => {
    setView((v) => (v === 'columns' ? 'map' : 'columns'));
    setHeldUntil(Date.now() + HOLD_MS);
  };

  return (
    <div className="cc-root" onClick={tap}>
      {isStale && (
        <div className="cc-stale">
          NOT UPDATING — last refreshed {fmtElapsed(now - lastOk)} ago{error ? ` · ${error}` : ''}
        </div>
      )}

      <div className="cc-header">
        <div className="cc-brand">NORTH GEORGIA SURVEYING</div>
        <div className="cc-date">{dateLabel}</div>
        <div className="cc-progress-wrap">
          <div className="cc-progress-text">
            <strong>{summary.completed}</strong> of <strong>{summary.total}</strong> jobs complete
            {summary.unassigned > 0 && ` · ${summary.unassigned} unassigned`}
          </div>
          <div className="cc-progress-bar">
            <div className="cc-progress-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="cc-clock">
          {new Date(serverNow).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </div>
      </div>

      {view === 'map' ? (
        <CommandCenterMap board={board} />
      ) : (
      <div className="cc-board">
        {board.crews.length === 0 && (
          <div className="cc-center">No active crews. Add them under Team → Crews.</div>
        )}
        {board.crews.map((crew) => {
          const cur = crew.current;
          // On site counts from arrival; en route counts from when they set off.
          const since = cur?.arrived_at || cur?.en_route_at;
          const elapsed = since ? serverNow - new Date(since).getTime() : null;
          return (
            <div
              key={crew.id}
              className={`cc-crew${crew.status === 'off' ? ' is-off' : ''}`}
              style={{ '--cc-crew-color': crew.color }}
            >
              <div className="cc-crew-head">
                <div className="cc-crew-name">{crew.name}</div>
                <div className="cc-crew-members">
                  {/* An ad-hoc card is somebody working outside their standing crew —
                      saying so stops it reading as a crew that was set up wrong. */}
                  {crew.is_adhoc
                    ? (crew.members.length > 1 ? 'Working together today' : 'Working alone today')
                    : (crew.members.map((m) => m.name).join(' · ') || 'No one assigned')}
                </div>
                {/* Colour is never the only signal — the word is always there too. */}
                <div className={`cc-status ${crew.status}`}>
                  {STATUS_LABEL[crew.status] || crew.status}
                </div>
              </div>

              <div className="cc-crew-body">
                {cur ? (
                  <div className="cc-current">
                    <JobLines job={cur} />
                    <div className="cc-timeline">
                      <div className={`cc-elapsed ${crew.status}`}>
                        {fmtElapsed(elapsed)}
                      </div>
                      {cur.arrived_at ? (
                        <div className="cc-arrived">
                          {/* "on site since" would be a lie for desk work. */}
                          {crew.status === 'office'
                            ? `${cur.work_type || 'Working'} since ${fmtClockTime(cur.arrived_at)}`
                            : `on site since ${fmtClockTime(cur.arrived_at)}`}
                        </div>
                      ) : cur.en_route_at ? (
                        <div className="cc-arrived">left at {fmtClockTime(cur.en_route_at)}</div>
                      ) : null}
                    </div>
                    {cur.on_site_names?.length > 0 && (
                      <div className="cc-onsite-names">{cur.on_site_names.join(', ')}</div>
                    )}
                  </div>
                ) : (
                  <div className="cc-empty">
                    {crew.status === 'off' ? 'Not clocked in' : 'On the clock, no job started'}
                  </div>
                )}

                <DeviceLine device={crew.device} />

                {crew.queue.length > 0 && (
                  <>
                    <div className="cc-queue-label">Next ({crew.queue.length})</div>
                    {crew.queue.map((j) => (
                      <div className="cc-queued" key={j.job_id}>
                        <JobLines job={j} />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {view === 'columns' && board.unassigned.length > 0 && (
        <div className="cc-footer">
          <div className="cc-footer-head">Unassigned today · {board.unassigned.length}</div>
          <div className="cc-unassigned-row">
            {board.unassigned.map((j) => (
              <div className="cc-chip" key={j.job_id}>
                <div className="cc-job-number">{j.job_number}</div>
                <div className="cc-client">{j.client_name || j.address || ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
